import React, { useState, useEffect } from 'react';
import {
  EnvelopeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { emailAPI, type EmailTemplate } from '../../services/api';
import { bookingPagesAPI } from '../../services/calendarsService';
import type { BookingPage } from '../../types/calendar';
import { EmailTemplateRichEditor } from '../../components/email/EmailTemplateRichEditor';
import { isEmailTemplateBodyEmpty, emailTemplateBodyPreview } from '../../utils/emailTemplateHtml';

const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  // LT-507: optional booking-page binding for the new template.
  const [newBookingPageId, setNewBookingPageId] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editBookingPageId, setEditBookingPageId] = useState<string>('');
  const [editOriginalBookingPageId, setEditOriginalBookingPageId] =
    useState<string>('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [createEditorKey, setCreateEditorKey] = useState(0);

  // LT-507: workspace booking pages, used by the editor's picker. Loaded
  // once on mount — refresh on creation is a follow-up if it ever
  // matters; the typical editor session keeps the page list stable.
  const [bookingPages, setBookingPages] = useState<BookingPage[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const list = await emailAPI.getTemplates();
      setTemplates(Array.isArray(list) ? list : []);
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  };

  const loadBookingPages = async () => {
    try {
      const list = await bookingPagesAPI.list({ is_active: true });
      setBookingPages(Array.isArray(list) ? list : []);
    } catch (e) {
      // Non-fatal — the booking-page picker just stays empty; the
      // template page is still usable for regular-template editing.
      setBookingPages([]);
    }
  };

  useEffect(() => {
    load();
    loadBookingPages();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const handleCreate = async () => {
    if (!newName.trim() || !newSubject.trim() || isEmailTemplateBodyEmpty(newBody)) return;
    setSaving(true);
    try {
      await emailAPI.createTemplate({
        name: newName.trim(),
        subject: newSubject.trim(),
        body: newBody.trim(),
        booking_page_id: newBookingPageId || null,
      });
      setToast({ type: 'success', message: 'Template created' });
      setAddingNew(false);
      setNewName('');
      setNewSubject('');
      setNewBody('');
      setNewBookingPageId('');
      setCreateEditorKey((k) => k + 1);
      load();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to create template' });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t: EmailTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditSubject(t.subject);
    setEditBody(t.body);
    setEditBookingPageId(t.booking_page_id || '');
    setEditOriginalBookingPageId(t.booking_page_id || '');
    setAddingNew(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditSubject('');
    setEditBody('');
    setEditBookingPageId('');
    setEditOriginalBookingPageId('');
  };

  // LT-507: persist the update. The four-state mutability guard lives
  // server-side; this helper just decides whether the operator needs to
  // see a confirm dialog first (only the non-null → different non-null
  // transition needs one).
  const persistUpdate = async (confirmChange: boolean) => {
    if (!editingId) return;
    setSaving(true);
    try {
      const bookingPageChanged =
        editBookingPageId !== editOriginalBookingPageId;
      await emailAPI.updateTemplate(editingId, {
        name: editName.trim(),
        subject: editSubject.trim(),
        body: editBody.trim(),
        ...(bookingPageChanged
          ? {
              booking_page_id: editBookingPageId || null,
              confirm_change: confirmChange || undefined,
            }
          : {}),
      });
      setToast({ type: 'success', message: 'Template updated' });
      cancelEdit();
      load();
    } catch (e: any) {
      // Surface the backend's 422 message inline. Block-rules and the
      // rebind-without-confirm refusal both reach this branch.
      setToast({
        type: 'error',
        message:
          e?.response?.data?.detail || 'Failed to update template',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !editName.trim() || !editSubject.trim() || isEmailTemplateBodyEmpty(editBody)) return;
    const oldId = editOriginalBookingPageId;
    const newId = editBookingPageId;
    // Non-null → different non-null: rebind. Show the confirm dialog
    // before submitting so the operator sees the impact on in-flight
    // sequences. The other transitions reach the backend either as
    // pass-throughs (null→null, identical) or as blocked 422s
    // (null→non-null and non-null→null with active usages).
    if (oldId && newId && oldId !== newId) {
      if (
        !window.confirm(
          "Active sequences may use this template — changing the booking page rebinds future bookings to the new calendar/owner. Continue?",
        )
      ) {
        return;
      }
      await persistUpdate(true);
      return;
    }
    await persistUpdate(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await emailAPI.deleteTemplate(id);
      setToast({ type: 'success', message: 'Template deleted' });
      load();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.response?.data?.detail || 'Failed to delete template' });
    }
  };

  return (
    <div className="relative mx-auto px-2 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
      {toast && (
        <div className="fixed top-20 sm:top-24 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-full mx-4">
          <div className={`p-3 rounded-lg flex items-center justify-between shadow-lg ${toast.type === 'success' ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
            <span className={`text-sm ${toast.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>{toast.message}</span>
            <button type="button" onClick={() => setToast(null)} className="text-gray-400 hover:text-white ml-2">×</button>
          </div>
        </div>
      )}

      <div className="mb-4 sm:mb-6 lg:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-slate-50">
              Email Templates
            </h1>
            <p className="text-gray-300 text-sm sm:text-base lg:text-lg mt-1">
              Create rich-text email templates with the editor below (bold, lists, links, colors). Subject line stays plain; use placeholders in both.
              When you have no templates yet, we add starter templates automatically; you can edit or remove them anytime.
            </p>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-300 hover:bg-cyan-500/20 transition-colors"
            >
              <InformationCircleIcon className="h-4 w-4" /> Placeholders
            </button>
            {!addingNew && (
              <button
                type="button"
                onClick={() => {
                  setAddingNew(true);
                  cancelEdit();
                  setNewName('');
                  setNewSubject('');
                  setNewBody('');
                  setCreateEditorKey((k) => k + 1);
                }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2 text-sm font-medium text-white hover:from-sky-600 hover:to-blue-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4" /> New Template
              </button>
            )}
          </div>
        </div>
      </div>

      {addingNew && (
        <div className="mb-6 rounded-xl border border-slate-600/50 bg-slate-800/60 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-white">Create New Template</h3>
          <input
            type="text"
            placeholder="Template name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
          />
          <input
            type="text"
            placeholder="Subject line (supports {{lead_name}}, etc.)"
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
          />
          <div>
            <p className="text-xs text-slate-400 mb-1">Message body</p>
            <EmailTemplateRichEditor
              key={`create-${createEditorKey}`}
              content={newBody}
              onChange={setNewBody}
              placeholder="Write your email… Use toolbar for bold, lists, links, color."
            />
          </div>
          <BookingPagePicker
            value={newBookingPageId}
            onChange={setNewBookingPageId}
            bookingPages={bookingPages}
            testId="new-email-template-booking-page"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setAddingNew(false);
                setNewName('');
                setNewSubject('');
                setNewBody('');
                setNewBookingPageId('');
                setCreateEditorKey((k) => k + 1);
              }}
              className="px-3 py-1.5 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || !newName.trim() || !newSubject.trim() || isEmailTemplateBodyEmpty(newBody)}
              className="px-3 py-1.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg hover:from-sky-600 hover:to-blue-700 transition-colors text-sm disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-400 py-12">Loading templates...</div>
      ) : templates.length === 0 && !addingNew ? (
        <div className="text-center py-16">
          <EnvelopeIcon className="mx-auto h-12 w-12 text-slate-600" />
          <h3 className="mt-3 text-lg font-medium text-slate-300">No email templates yet</h3>
          <p className="mt-1 text-sm text-slate-500">Create a template to send email to your leads.</p>
          <button
            type="button"
            onClick={() => {
              setAddingNew(true);
              setNewName('');
              setNewSubject('');
              setNewBody('');
              setCreateEditorKey((k) => k + 1);
            }}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2 text-sm font-medium text-white hover:from-sky-600 hover:to-blue-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" /> Create First Template
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => {
            const isEditing = editingId === t.id;
            return (
              <div
                key={t.id}
                className="rounded-xl border border-slate-600/50 bg-slate-800/60 p-4 transition-colors hover:border-slate-500/50"
              >
                {isEditing ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    />
                    <input
                      type="text"
                      value={editSubject}
                      onChange={(e) => setEditSubject(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                    />
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Message body</p>
                      <EmailTemplateRichEditor
                        key={editingId || 'edit'}
                        content={editBody}
                        onChange={setEditBody}
                        placeholder="Write your email…"
                      />
                    </div>
                    <BookingPagePicker
                      value={editBookingPageId}
                      onChange={setEditBookingPageId}
                      bookingPages={bookingPages}
                      testId={`edit-email-template-booking-page-${t.id}`}
                    />
                    <div className="flex gap-2">
                      <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" title="Cancel">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={handleUpdate}
                        disabled={saving || !editName.trim() || !editSubject.trim() || !editBody.trim()}
                        className="p-1.5 text-green-400 hover:text-green-300 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
                        title="Save"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-white truncate">{t.name}</h3>
                      <p className="mt-1 text-xs text-sky-300/90 truncate">Subject: {t.subject}</p>
                      {t.booking_page_id && (
                        <p className="mt-1 text-[11px] text-emerald-300 font-mono">
                          Booking-bearing · books against{' '}
                          {bookingPages.find((p) => p.id === t.booking_page_id)?.slug
                            || bookingPages.find((p) => p.id === t.booking_page_id)?.title
                            || t.booking_page_id}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-slate-400 line-clamp-3">{emailTemplateBodyPreview(t.body, 200)}</p>
                      <p className="mt-2 text-xs text-slate-500">
                        Created {new Date(t.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => startEdit(t)} className="p-1.5 text-cyan-400 hover:text-cyan-300 rounded-lg hover:bg-white/10 transition-colors" title="Edit">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => handleDelete(t.id)} className="p-1.5 text-red-400 hover:text-red-300 rounded-lg hover:bg-white/10 transition-colors" title="Delete">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showInfoModal && (
        <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto custom-scrollbar flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={() => setShowInfoModal(false)} />
          <div className="relative z-10 w-full max-w-lg rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
              <div className="flex items-center gap-2">
                <InformationCircleIcon className="w-6 h-6 text-cyan-400" />
                <h2 className="text-lg font-semibold text-white">Placeholders (subject + body)</h2>
              </div>
              <button type="button" onClick={() => setShowInfoModal(false)} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors" aria-label="Close">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-gray-300">
                Use these in the subject and anywhere in the message body. Type each placeholder as normal text—avoid splitting it across formatting so it replaces correctly. They are substituted when the email is sent.
              </p>
              <div className="rounded-lg border border-slate-600/50 bg-slate-700/30 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600/50">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-cyan-400 uppercase">Placeholder</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-cyan-400 uppercase">Replaced with</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-600/30">
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{lead_name}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">Lead name</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{agent_name}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">Your display name</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5"><code className="rounded bg-slate-600/50 px-1.5 py-0.5 text-emerald-300 text-xs font-mono">{'{{callback_number}}'}</code></td>
                      <td className="px-4 py-2.5 text-gray-300">Your outbound / callback number (when configured)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={() => setShowInfoModal(false)} className="w-full px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 transition-colors text-sm">
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// LT-507 — booking-page picker shared by the create + edit forms.
// Renders a labelled <select> backed by the workspace's active booking
// pages. Empty value = regular template; any UUID = booking-bearing.
interface BookingPagePickerProps {
  value: string;
  onChange: (id: string) => void;
  bookingPages: BookingPage[];
  testId: string;
}

const BookingPagePicker: React.FC<BookingPagePickerProps> = ({
  value,
  onChange,
  bookingPages,
  testId,
}) => (
  <div className="rounded-lg border border-slate-600/40 bg-slate-900/40 p-3">
    <label className="block text-xs text-slate-300">
      <span className="font-mono">Booking page (optional)</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 w-full px-2 py-1.5 bg-slate-900 border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
      >
        <option value="">— Regular template (no booking page) —</option>
        {bookingPages.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title || p.slug || p.id}
          </option>
        ))}
      </select>
    </label>
    <p className="mt-1.5 text-[11px] text-slate-500">
      Booking-bearing templates can only be picked by the Book-Meeting
      sequence step. Regular templates are used by Email steps.
    </p>
  </div>
);

export default EmailTemplatesPage;
